import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import {
  PhArrowUp,
  PhArrowDown,
  PhDotsSixVertical,
  PhX,
  PhPlus,
  PhSortAscending,
  PhArrowCounterClockwise,
} from 'ember-phosphor-icons';
import { getTypeConfig } from './field-list';
import type { SortEntry } from '../../utils/sort-helpers';
import type { FieldOption } from '../../utils/opensearch-field-mapper';

export interface SortPanelSignature {
  Args: {
    sorts: SortEntry[];
    columns: FieldOption[];
    onAdd: (key: string, direction: 'asc' | 'desc') => void;
    onRemove: (key: string) => void;
    onMove: (fromIndex: number, toIndex: number) => void;
    onToggleDirection: (key: string) => void;
    onSetDirection: (key: string, direction: 'asc' | 'desc') => void;
    onReset?: () => void;
    savedSorts?: SortEntry[];
    isAggregated?: boolean;
    groupByFieldKeys?: string[];
  };
}

class SortPanel extends Component<SortPanelSignature> {
  @tracked dragFromIndex: number | null = null;
  @tracked dropLinePosition: number | null = null;
  private dragEnterCount = 0;
  @tracked isAddOpen = false;

  get sortableFields(): Array<{ key: string; label: string }> {
    const existingKeys = new Set(this.args.sorts.map((s) => s.key));
    return this.args.columns
      .filter((col) => {
        if (existingKeys.has(col.key)) return false;
        if (!col.sortPath) return false;
        return true;
      })
      .map((col) => ({ key: col.sortPath!, label: col.label }));
  }

  get hasSorts() {
    return this.args.sorts.length > 0;
  }

  get canReset() {
    if (!this.args.onReset || !this.args.savedSorts) return false;
    const saved = this.args.savedSorts;
    const current = this.args.sorts;
    if (saved.length !== current.length) return true;
    return saved.some((s, i) => s.key !== current[i]?.key || s.direction !== current[i]?.direction);
  }

  fieldForKey = (key: string): FieldOption | undefined => {
    return this.args.columns.find(
      (col) => col.key === key || col.sortPath === key || col.path === key
    );
  };

  fieldLabel = (key: string): string => {
    const field = this.fieldForKey(key);
    return field?.label || key;
  };

  fieldType = (key: string): string => {
    const field = this.fieldForKey(key);
    return field?.type || 'text';
  };

  typeConfig = (key: string) => {
    return getTypeConfig(this.fieldType(key));
  };

  toggleAddDropdown = () => {
    this.isAddOpen = !this.isAddOpen;
  };

  closeAddDropdown = () => {
    this.isAddOpen = false;
  };

  handleReset = () => {
    this.args.onReset?.();
  };

  handleAddSort = (fieldKey: string) => {
    this.args.onAdd(fieldKey, 'asc');
    this.isAddOpen = false;
  };

  // Drag-and-drop reordering
  isDragging = (index: number) => {
    return this.dragFromIndex === index;
  };

  showLineBefore = (index: number) => {
    return this.dropLinePosition === index;
  };

  get showLineAfterLast() {
    return this.dropLinePosition === this.args.sorts.length;
  }

  handleDragStart = (index: number, event: DragEvent) => {
    this.dragFromIndex = index;
    this.dragEnterCount = 0;
    event.dataTransfer?.setData('text/plain', String(index));
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  };

  handleContainerDragOver = (event: DragEvent) => {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    const container = event.currentTarget as HTMLElement;
    const children = Array.from(container.querySelectorAll('[data-sort-index]'));
    let gapPosition = children.length;
    for (const child of children) {
      const rect = child.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      if (event.clientY < midY) {
        gapPosition = Number(child.getAttribute('data-sort-index'));
        break;
      }
    }
    if (
      this.dragFromIndex !== null &&
      (gapPosition === this.dragFromIndex || gapPosition === this.dragFromIndex + 1)
    ) {
      this.dropLinePosition = null;
    } else {
      this.dropLinePosition = gapPosition;
    }
  };

  handleContainerDragEnter = (event: DragEvent) => {
    event.preventDefault();
    this.dragEnterCount++;
  };

  handleContainerDragLeave = () => {
    this.dragEnterCount--;
    if (this.dragEnterCount <= 0) {
      this.dragEnterCount = 0;
      this.dropLinePosition = null;
    }
  };

  handleContainerDrop = (event: DragEvent) => {
    event.preventDefault();
    const fromIndex = this.dragFromIndex;
    const toGap = this.dropLinePosition;
    this.dragFromIndex = null;
    this.dropLinePosition = null;
    this.dragEnterCount = 0;
    if (fromIndex !== null && toGap !== null) {
      const toIndex = toGap > fromIndex ? toGap - 1 : toGap;
      if (toIndex !== fromIndex) {
        this.args.onMove(fromIndex, toIndex);
      }
    }
  };

  handleDragEnd = () => {
    this.dragFromIndex = null;
    this.dropLinePosition = null;
    this.dragEnterCount = 0;
  };

  <template>
    <div class="tio-bg-surface rounded-lg border tio-border-default p-3" data-test-sort-panel>
      <div class="tio-row-between mb-2">
        <div class="tio-row-snug">
          <PhSortAscending @size={{14}} class="tio-fg-muted" />
          <span class="tio-text-sm-semibold tio-fg-secondary">Sort</span>
          {{#if this.hasSorts}}
            <span class="tio-text-xs-medium tio-bg-sunken tio-fg-muted px-1.5 py-0.5 rounded">
              {{@sorts.length}}
            </span>
          {{/if}}
        </div>
        <span class="tio-row-snug">
          {{#if this.canReset}}
            <Button
              @appearance="outlined"
              @size="sm"
              @icon={{PhArrowCounterClockwise}}
              data-test-sort-reset-button
              {{on "click" this.handleReset}}
            >
              Reset
            </Button>
          {{/if}}
          <span class="relative">
            <Button
              @appearance="outlined"
              @size="sm"
              @icon={{PhPlus}}
              @isDisabled={{eq this.sortableFields.length 0}}
              data-test-sort-add-button
              {{on "click" this.toggleAddDropdown}}
            >
              Add sort
            </Button>
            {{#if this.isAddOpen}}
              <div
                class="app-popover absolute right-0 top-full z-30 mt-1 w-56 max-h-64 overflow-y-auto py-1"
                data-test-sort-add-dropdown
              >
                {{#each this.sortableFields as |option|}}
                  <button
                    type="button"
                    class="app-popover-option app-popover-option--sm"
                    data-test-sort-add-option={{option.key}}
                    {{on "click" (fn this.handleAddSort option.key)}}
                  >
                    {{option.label}}
                  </button>
                {{/each}}
                <button
                  type="button"
                  class="app-popover-option app-popover-option--sm border-t tio-border-subtle"
                  {{on "click" this.closeAddDropdown}}
                >
                  Cancel
                </button>
              </div>
            {{/if}}
          </span>
        </span>
      </div>

      {{#if this.hasSorts}}
        <div
          class="tio-col-extra-tight"
          {{on "dragover" this.handleContainerDragOver}}
          {{on "dragenter" this.handleContainerDragEnter}}
          {{on "dragleave" this.handleContainerDragLeave}}
          {{on "drop" this.handleContainerDrop}}
        >
          {{#each @sorts as |entry index|}}
            {{#if (this.showLineBefore index)}}
              <div class="h-0.5 tio-bg-brand-accent rounded-full mx-1" aria-hidden="true"></div>
            {{/if}}
            <div
              data-sort-index={{index}}
              data-test-sort-entry={{entry.key}}
              class="tio-row-snug tio-bg-sunken border tio-border-default rounded-md px-2 py-1.5 text-sm transition-opacity
                {{if (this.isDragging index) 'opacity-30'}}"
            >
              <span
                draggable="true"
                role="button"
                aria-label="Drag to reorder"
                class="cursor-grab shrink-0"
                {{on "dragstart" (fn this.handleDragStart index)}}
                {{on "dragend" this.handleDragEnd}}
              >
                <PhDotsSixVertical
                  @size={{14}}
                  class="tio-fg-muted"
                  aria-hidden="true"
                  role="presentation"
                />
              </span>

              <span
                class="tio-text-xs-medium tio-fg-muted w-4 text-center shrink-0"
              >{{this.sortPositionLabel index}}</span>

              <span class="tio-fg-secondary truncate min-w-0">{{this.fieldLabel entry.key}}</span>

              {{#let (this.typeConfig entry.key) as |tc|}}
                <Chip @size="sm" @intent={{tc.intent}} @appearance="faded">
                  {{tc.label}}
                </Chip>
              {{/let}}

              <span class="tio-row-flush gap-0.5 ml-auto shrink-0">
                {{#if (eq entry.direction "asc")}}
                  <button
                    type="button"
                    aria-label="Sorting ascending — click for descending"
                    aria-pressed="true"
                    class="tio-report-sort-direction--active tio-inline-row-center rounded-md tio-bg-success-soft p-1 tio-fg-success-body"
                    data-test-sort-direction={{entry.key}}
                    {{on "click" (fn @onToggleDirection entry.key)}}
                  >
                    <PhArrowUp @size={{14}} @weight="bold" />
                  </button>
                {{else}}
                  <button
                    type="button"
                    aria-label="Sorting descending — click for ascending"
                    aria-pressed="true"
                    class="tio-report-sort-direction--active tio-inline-row-center rounded-md tio-bg-success-soft p-1 tio-fg-success-body"
                    data-test-sort-direction={{entry.key}}
                    {{on "click" (fn @onToggleDirection entry.key)}}
                  >
                    <PhArrowDown @size={{14}} @weight="bold" />
                  </button>
                {{/if}}

                <button
                  type="button"
                  aria-label="Remove sort"
                  class="p-0.5 tio-fg-muted hover:tio-fg-danger"
                  data-test-sort-remove={{entry.key}}
                  {{on "click" (fn @onRemove entry.key)}}
                >
                  <PhX @size={{12}} />
                </button>
              </span>
            </div>
          {{/each}}
          {{#if this.showLineAfterLast}}
            <div class="h-0.5 tio-bg-brand-accent rounded-full mx-1" aria-hidden="true"></div>
          {{/if}}
        </div>
      {{else}}
        <div
          class="tio-row-center h-10 text-xs tio-fg-muted border border-dashed tio-border-default rounded-md"
        >
          No sort applied — click "Add sort" to order results
        </div>
      {{/if}}
    </div>
  </template>

  sortPositionLabel = (index: number): string => {
    return String(index + 1);
  };
}

export default SortPanel;
